import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "chameleon/lib/components/alert/alert";

import {
  paymentMessagesAtom,
  paymentRequirementsAtom,
} from "checkout-experience/atoms/payment-atoms";
import { FormErrorCode } from "checkout-query-client/types/form-types";

import { PayRequestErrorCode } from "frontend-client";

const ErrorBlock = ({
  paymentError,
  formError,
}: {
  paymentError?: PayRequestErrorCode;
  formError?: FormErrorCode;
}) => {
  const { t } = useTranslation();
  const paymentRequirements = useAtomValue(paymentRequirementsAtom);
  const paymentMessages = useAtomValue(paymentMessagesAtom);

  useEffect(() => {
    if (paymentError || formError) {
      // Make sure we're scrolling to the top of the page when error occurs
      const header = document.querySelector("header");
      window.scrollTo({ top: header?.clientHeight, behavior: "smooth" });
    }
  }, [paymentError, formError]);

  // Used to suppress errors if a payment requirement needs to be or is being fulfilled;
  const hasPaymentRequirements = paymentRequirements
    ? !!Object.keys(paymentRequirements).length
    : false;

  const getAlertElement = () => {
    // Error can be 0 which is valid so need to check undefined/null
    if (
      paymentError !== undefined &&
      paymentError !== null &&
      !hasPaymentRequirements
    ) {
      if (paymentError === PayRequestErrorCode.INVALID_ROUTING_NUMBER) {
        return (
          <Alert
            variant="Error"
            title={t("paymentError.invalidRoutingTitle")}
            description={t("paymentError.invalidRoutingMessage")}
          />
        );
      }

      if (paymentError === PayRequestErrorCode.INVALID_CVV) {
        return (
          <Alert
            variant="Error"
            title={t("paymentError.invalidCVVLengthTitle")}
            description={t("paymentError.invalidCVVLengthMessage")}
          />
        );
      }

      // TODO as more recoverable errors are added create new error blocks here.

      // suppress error if paymentRequirement is being fulfilled in paymentMessages
      if (
        paymentMessages.threeDsCompletionMessage?.status === "STATUS_SUCCESS"
      ) {
        return <></>;
      }

      // catch all for unsuppressed errors
      return (
        <Alert
          variant="Error"
          title={t("paymentError.title")}
          description={t("paymentError.message")}
        />
      );
    }
    // Error can be 0 which is valid so need to check undefined/null
    if (formError !== undefined && formError !== null) {
      return (
        <Alert
          variant="Error"
          title={
            formError === FormErrorCode.cardTypeError
              ? t("paymentError.title")
              : t("formError.title")
          }
          description={
            formError === FormErrorCode.cardTypeError
              ? t("formError.unableToGetCardType")
              : t("formError.message")
          }
        />
      );
    }

    return <></>;
  };

  return getAlertElement();
};

export default ErrorBlock;
